/*
Event slide of the today slider. Its own component because it needs a ref on its root:
through that ref useContentImpression tracks the content impression as soon as the slide
is 80 % visible (see src/utils/matomo.ts).

The styles still come from EventsTodaySlider.module.scss — markup and layout of the slide
are unchanged.
*/

'use client';

import { CategoryLink } from '@/sanity/fetchCategories';
import { EventCard } from '@/sanity/fetchEvents';
import { URL_PATH_EVENT } from '@/shared/constants';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_CONTENT_KINDS,
  MATOMO_NAMES,
  buildContentPiece,
  trackMatomoEvent,
} from '@/utils/matomo';
import { useContentImpression } from '@/hooks/useContentImpression';
import { ContentBlockProvider } from '../base/ContentTracking';
import HIHImage from '../base/HIHImage';
import HIHLink from '../base/Link';
import { CategoryTag } from '../base/Tag';
import ActionFavorite from '../action/favorite/ActionFavorite';
import EventFacts from './EventFacts';
import styles from './EventsTodaySlider.module.scss';

export default function EventsTodaySliderEventSlide({
  event,
  position,
  locationOnClick,
  ticketLinkOnClick,
}: {
  event: EventCard;
  /** 1-based slide position, sent as the value of the existing Matomo event. */
  position: number;
  locationOnClick: () => void;
  ticketLinkOnClick: () => void;
}) {
  const eventPath = `/${URL_PATH_EVENT}/${event.slug.current}`;

  const { ref, block, trackInteraction } = useContentImpression({
    contentPiece: buildContentPiece(MATOMO_CONTENT_KINDS.EVENT, event.slug.current),
    contentTarget: eventPath,
  });

  const handleOpenClick = () =>
    trackInteraction(MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET);

  // The event still hangs on the image link alone, so the existing row in the events
  // report keeps its meaning. The content interaction counts both links.
  const handleImageClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.EVENT_TIP_CLICK,
      MATOMO_NAMES.TODAY_RECOMMENDATION_CLICK,
      position,
    );
    handleOpenClick();
  };

  return (
    <ContentBlockProvider block={block}>
      <div className={styles.slideGrid} ref={ref}>
        <div className={styles.slideImageCol}>
          <HIHLink href={eventPath} onClick={handleImageClick}>
            <div className={styles.slideImageWrapper}>
              <HIHImage
                image={event.coverImage}
                fill
                sizes="(min-width: 1024px) 50vw, 100vw"
                priority
                lazy={false}
                className={styles.slideImage}
                hasPlaceholder
              />
            </div>
          </HIHLink>
          <ActionFavorite
            id={event._id}
            className={styles.favoriteButton}
            bookmarkType="Event"
          />
        </div>
        <div className={styles.slideInfoCol}>
          <div>
            <div className={styles.categoryTags}>
              {event.categories?.slice(0, 2).map((category: CategoryLink) => (
                <CategoryTag key={category._id} category={category} />
              ))}
            </div>
            <HIHLink href={eventPath} onClick={handleOpenClick}>
              <div className={styles.slideTitle}>{event.title}</div>
            </HIHLink>

            <EventFacts
              event={event}
              className={styles.eventFactsSpacing}
              locationOnClick={locationOnClick}
              ticketLinkOnClick={ticketLinkOnClick}
            />
          </div>
        </div>
      </div>
    </ContentBlockProvider>
  );
}
